import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../types/api-errors';
import { useToast } from '../contexts/ToastContext';

export const useSupportTickets = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { showToast } = useToast();

    const getCategories = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiService.getSupportCategories();
            return data.categories;
        } catch (err: any) {
            setError(err?.message || 'Failed to load categories');
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createTicket = useCallback(async (data: { category: string; description: string; email?: string; fullName?: string; preferredContactMethod?: string }) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiService.createSupportTicket(data);
            showToast(result.message || 'Ticket created successfully', 'success');
            return result;
        } catch (err: any) {
            const msg = getApiErrorMessage(err, 'Failed to create ticket'); // AB#1919 (WS-5b)
            setError(msg);
            showToast(msg, 'error');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const getTickets = useCallback(async (status?: string, page = 1) => {
        setLoading(true);
        try {
            return await apiService.getSupportTickets(status, page);
        } catch (err: any) {
            setError(err?.message || 'Failed to load tickets');
            return { tickets: [], totalCount: 0, page: 1, pageSize: 20 };
        } finally {
            setLoading(false);
        }
    }, []);

    const getTicketById = useCallback(async (id: number) => {
        setLoading(true);
        try {
            return await apiService.getSupportTicketById(id);
        } catch (err: any) {
            setError(err?.message || 'Failed to load ticket');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const replyToTicket = useCallback(async (id: number, message: string) => {
        setLoading(true);
        try {
            await apiService.replyToSupportTicket(id, message);
            showToast('Reply sent successfully', 'success');
            return true;
        } catch (err: any) {
            showToast('Failed to send reply', 'error');
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const closeTicket = useCallback(async (id: number) => {
        setLoading(true);
        try {
            await apiService.closeSupportTicket(id);
            showToast('Ticket closed', 'success');
            return true;
        } catch (err: any) {
            showToast('Failed to close ticket', 'error');
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    return { getCategories, createTicket, getTickets, getTicketById, replyToTicket, closeTicket, loading, error };
};
